/**
 * Real connectivity: navigator.onLine + periodic /health heartbeat (PRD §10.1).
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { HEALTH_CHECK_INTERVAL_MS, HEALTH_TIMEOUT_MS, HEALTH_URL } from '../offline/offlineConfig.js';

const ConnectivityContext = createContext(null);

async function pingHealth(signal) {
  const res = await fetch(HEALTH_URL, {
    method: 'GET',
    cache: 'no-store',
    credentials: 'same-origin',
    signal,
  });
  return res.ok;
}

export function ConnectivityProvider({ children }) {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const [isReachable, setIsReachable] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const [lastCheckedAt, setLastCheckedAt] = useState(null);
  const abortRef = useRef(null);
  const intervalRef = useRef(null);
  const checkGenRef = useRef(0);

  const checkReachability = useCallback(async () => {
    if (typeof window === 'undefined') return false;
    if (!navigator.onLine) {
      setIsReachable(false);
      setLastCheckedAt(new Date().toISOString());
      return false;
    }
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const gen = ++checkGenRef.current;
    // Abort a ping that hangs (server reachable at TCP level but not responding).
    const timeoutId = window.setTimeout(() => controller.abort(), HEALTH_TIMEOUT_MS);
    try {
      const ok = await pingHealth(controller.signal);
      if (gen !== checkGenRef.current) return false;
      setIsReachable(ok);
      setLastCheckedAt(new Date().toISOString());
      return ok;
    } catch {
      if (gen !== checkGenRef.current) return false;
      setIsReachable(false);
      setLastCheckedAt(new Date().toISOString());
      return false;
    } finally {
      window.clearTimeout(timeoutId);
      if (abortRef.current === controller) abortRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const onOnline = () => {
      setIsOnline(true);
      checkReachability();
    };
    const onOffline = () => {
      checkGenRef.current += 1;
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      setIsOnline(false);
      setIsReachable(false);
      setLastCheckedAt(new Date().toISOString());
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') checkReachability();
    };

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    document.addEventListener('visibilitychange', onVisibility);

    checkReachability();
    intervalRef.current = window.setInterval(checkReachability, HEALTH_CHECK_INTERVAL_MS);

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      document.removeEventListener('visibilitychange', onVisibility);
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [checkReachability]);

  const value = {
    isOnline,
    isReachable,
    lastCheckedAt,
    checkReachability,
  };

  return (
    <ConnectivityContext.Provider value={value}>{children}</ConnectivityContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useConnectivity() {
  const ctx = useContext(ConnectivityContext);
  if (!ctx) {
    throw new Error('useConnectivity must be used within ConnectivityProvider');
  }
  return ctx;
}
